import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Field } from '@/components/common/Field'
import { Modal } from '@/components/common/Modal'
import { inputClass, primaryButtonClass, selectClass } from '@/components/common/formStyles'
import { formatNumber } from '@/lib/format'
import { getStopStatus } from '@/lib/stopApi'
import { useGenerateOrderTicket, useMarkTicketCopied } from '@/lib/useOrderTicket'
import type { OrderTIF, OrderTicket, OrderType } from '@/types/orderTicket'

interface OrderTicketModalProps {
  open: boolean
  onClose: () => void
  positionId: number | null
  alertId?: number | null
  positionName?: string
  direction?: 'sell' | 'buy'
  defaultOrderType?: OrderType
  prefillEurAmount?: number | null
}

const LIMIT_SPREAD_PCT = 0.02

const ORDER_TYPE_OPTIONS_BY_DIRECTION: Record<
  'sell' | 'buy',
  Array<{ value: OrderType; label: string }>
> = {
  sell: [
    { value: 'stop_limit', label: 'Stop-Limit (STP LMT)' },
    { value: 'market', label: 'Market (MKT)' },
  ],
  buy: [
    { value: 'market', label: 'Market (MKT)' },
    { value: 'limit', label: 'Limit (LMT)' },
  ],
}

function calcDefaultLimit(stop: string): string {
  const num = Number(stop)
  if (!Number.isFinite(num) || num <= 0) return ''
  return formatNumber(num * (1 - LIMIT_SPREAD_PCT))
}

export function OrderTicketModal({
  open,
  onClose,
  positionId,
  alertId = null,
  positionName,
  direction = 'sell',
  defaultOrderType = 'stop_limit',
  prefillEurAmount = null,
}: OrderTicketModalProps) {
  const [orderType, setOrderType] = useState<OrderType>(defaultOrderType)
  const [quantity, setQuantity] = useState('')
  const [stopPrice, setStopPrice] = useState('')
  const [limitPrice, setLimitPrice] = useState('')
  const [tif, setTif] = useState<OrderTIF>('GTC')
  const [error, setError] = useState<string | null>(null)
  const [generated, setGenerated] = useState<OrderTicket | null>(null)
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle')

  const generate = useGenerateOrderTicket(positionId ?? 0)
  const markCopied = useMarkTicketCopied()

  // Stop-Level + Currency aus Position laden, sobald Modal mit Position öffnet.
  const stopStatusQuery = useQuery({
    queryKey: ['stopStatus', positionId],
    queryFn: () => getStopStatus(positionId!),
    enabled: open && positionId !== null,
    staleTime: 5_000,
  })

  // Reset, wenn Modal wieder geöffnet wird oder Position wechselt.
  useEffect(() => {
    if (!open) return
    setOrderType(defaultOrderType)
    setQuantity('')
    setStopPrice('')
    setLimitPrice('')
    setTif('GTC')
    setError(null)
    setGenerated(null)
    setCopyState('idle')
  }, [open, positionId, defaultOrderType])

  // Stop-Level aus Server vorbefüllen, wenn vorhanden (nur bei sell).
  useEffect(() => {
    if (direction !== 'sell') return
    if (!stopStatusQuery.data) return
    const lvl = stopStatusQuery.data.stop_level
    if (lvl !== null && stopPrice === '') {
      const stopStr = formatNumber(lvl)
      setStopPrice(stopStr)
      setLimitPrice(calcDefaultLimit(stopStr))
    }
    // limit nur initialisieren, sobald Stop-Wert vorliegt — User-Edits werden
    // nicht überschrieben.
  }, [direction, stopStatusQuery.data, stopPrice])

  // Quantity pre-fill aus prefillEurAmount / current_price (z.B. für Kauf-Flow).
  useEffect(() => {
    if (!stopStatusQuery.data || !prefillEurAmount) return
    if (quantity !== '') return
    const price = Number(stopStatusQuery.data.current_price)
    if (!Number.isFinite(price) || price <= 0) return
    const q = prefillEurAmount / price
    setQuantity(q.toFixed(4))
  }, [stopStatusQuery.data, prefillEurAmount, quantity])

  // Buy-Limit pre-fill: aktueller Kurs − 2 %.
  useEffect(() => {
    if (direction !== 'buy' || orderType !== 'limit') return
    if (!stopStatusQuery.data || limitPrice !== '') return
    const cur = Number(stopStatusQuery.data.current_price)
    if (!Number.isFinite(cur) || cur <= 0) return
    setLimitPrice(formatNumber(cur * (1 - LIMIT_SPREAD_PCT)))
  }, [direction, orderType, stopStatusQuery.data, limitPrice])

  const handleStopChange = (val: string) => {
    setStopPrice(val)
    setLimitPrice(calcDefaultLimit(val))
  }

  const handleGenerate = async () => {
    setError(null)
    if (positionId === null) return

    const qNum = Number(quantity)
    if (!Number.isFinite(qNum) || qNum <= 0) {
      setError('Stückzahl muss > 0 sein.')
      return
    }

    if (orderType === 'stop_limit') {
      if (!stopPrice || !limitPrice) {
        setError('Stop- und Limit-Preis sind bei Stop-Limit Pflicht.')
        return
      }
    } else if (orderType === 'limit') {
      if (!limitPrice) {
        setError('Limit-Preis ist bei Limit-Order Pflicht.')
        return
      }
    }

    const body = {
      action: direction,
      order_type: orderType,
      quantity,
      time_in_force: tif,
      related_alert_id: alertId,
      ...(orderType === 'stop_limit' ? { stop_price: stopPrice, limit_price: limitPrice } : {}),
      ...(orderType === 'limit' ? { limit_price: limitPrice } : {}),
    }

    try {
      const ticket = await generate.mutateAsync(body)
      setGenerated(ticket)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Generierung fehlgeschlagen')
    }
  }

  const handleCopy = async () => {
    if (!generated) return
    try {
      await navigator.clipboard.writeText(generated.ticket_text)
      setCopyState('copied')
      // markCopied ist Best-Effort — UI-Feedback hängt nicht vom Server-Erfolg ab.
      markCopied.mutate(generated.id)
      setTimeout(() => setCopyState('idle'), 2_000)
    } catch {
      setError('Zwischenablage nicht verfügbar — Text manuell markieren.')
    }
  }

  const verbPrefix = direction === 'sell' ? 'Verkauf' : 'Kauf'
  const title = positionName
    ? `${verbPrefix}-Order · ${positionName}`
    : `${verbPrefix}-Order generieren`

  return (
    <Modal open={open} onClose={onClose} title={title}>
      {generated ? (
        <div className="flex flex-col gap-5">
          <div className="border border-hairline bg-void px-4 py-3">
            <p className="tracked-caps text-[10px] text-faint">Copy-Paste nach IBKR</p>
            <pre className="mt-3 whitespace-pre-wrap font-mono text-xs leading-relaxed text-ink">
              {generated.ticket_text}
            </pre>
          </div>
          <p className="font-mono text-[11px] text-muted">
            Hinweis: Sextant führt keine Orders aus. Trage den Text in IBKR ein und bestätige dort
            manuell.
          </p>
          {error && (
            <p role="alert" className="text-sm text-loss">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setGenerated(null)}
              className="border border-hairline px-4 py-2 font-mono text-xs text-muted transition-colors hover:text-bernstein"
            >
              Neu erzeugen
            </button>
            <button type="button" onClick={handleCopy} className={primaryButtonClass}>
              {copyState === 'copied' ? 'Kopiert ✓' : 'Kopieren'}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {direction === 'sell' && stopStatusQuery.data && (
            <div className="border border-hairline bg-void px-4 py-3 font-mono text-xs text-muted">
              <p>
                Stop-Level:{' '}
                <span className="text-ink">{formatNumber(stopStatusQuery.data.stop_level)}</span>
              </p>
              <p className="mt-1">
                Aktueller Kurs:{' '}
                <span className="text-ink">{formatNumber(stopStatusQuery.data.current_price)}</span>
              </p>
            </div>
          )}

          <Field label="Order-Typ">
            <select
              value={orderType}
              onChange={(e) => setOrderType(e.target.value as OrderType)}
              className={selectClass}
            >
              {ORDER_TYPE_OPTIONS_BY_DIRECTION[direction].map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Stückzahl">
            <input
              type="number"
              min="0"
              step="any"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="z.B. 52"
              className={inputClass}
            />
          </Field>

          {orderType === 'stop_limit' && (
            <Field label="Stop-Preis">
              <input
                type="number"
                min="0"
                step="0.01"
                value={stopPrice}
                onChange={(e) => handleStopChange(e.target.value)}
                placeholder="z.B. 63.80"
                className={inputClass}
              />
            </Field>
          )}

          {(orderType === 'stop_limit' || orderType === 'limit') && (
            <Field
              label={orderType === 'limit' ? 'Limit-Preis' : 'Limit-Preis (Default Stop − 2 %)'}
            >
              <input
                type="number"
                min="0"
                step="0.01"
                value={limitPrice}
                onChange={(e) => setLimitPrice(e.target.value)}
                placeholder="z.B. 62.50"
                className={inputClass}
              />
            </Field>
          )}

          <Field label="Time in Force">
            <select
              value={tif}
              onChange={(e) => setTif(e.target.value as OrderTIF)}
              className={selectClass}
            >
              <option value="GTC">GTC (Good-Till-Cancelled)</option>
              <option value="DAY">DAY</option>
            </select>
          </Field>

          {error && (
            <p role="alert" className="text-sm text-loss">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="border border-hairline px-4 py-2 font-mono text-xs text-muted transition-colors hover:text-bernstein"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generate.isPending || positionId === null}
              className={primaryButtonClass}
            >
              {generate.isPending ? 'Generiere …' : 'Generieren'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
